/**
 * Notification Templates Page
 * Manage notification templates and send test notifications
 * Merged from PushTestPage - includes stats, device tokens, and logs
 */

import { useState } from 'react';
import { Send, LayoutList, Smartphone, ScrollText } from 'lucide-react';
import { Modal } from '@/components/common/Modal';
import { useI18n } from '@/i18n';
import {
  TemplateList,
  TemplateEditor,
  NotificationForm,
  createDefaultNotificationFormValue,
  useSendTestNotification,
} from '@/features/notification-templates';
import {
  DeviceTokens,
  NotificationLogs,
  PushStats,
  UserSelector,
} from '@/features/push-notifications';
import type { NotificationTemplate, NotificationFormValue } from '@/features/notification-templates';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

type TabType = 'templates' | 'send-test';
type EditorMode = 'create' | 'edit' | null;
type RightPanelTab = 'tokens' | 'logs';

export function NotificationTemplatesPage() {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState<TabType>('templates');
  const [editorMode, setEditorMode] = useState<EditorMode>(null);
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null);

  // Right panel tab for send-test view
  const [rightPanelTab, setRightPanelTab] = useState<RightPanelTab>('tokens');

  // Send test state
  const [target, setTarget] = useState<'user' | 'all'>('user');
  const [userIds, setUserIds] = useState<string[]>([]);
  const [formValue, setFormValue] = useState<NotificationFormValue>(createDefaultNotificationFormValue());
  const [sendResult, setSendResult] = useState<{ success: boolean; message: string } | null>(null);

  const sendTestMutation = useSendTestNotification();

  const handleCreateClick = () => {
    setEditorMode('create');
    setEditingTemplate(null);
  };

  const handleEditClick = (template: NotificationTemplate) => {
    setEditorMode('edit');
    setEditingTemplate(template);
  };

  const handleEditorClose = () => {
    setEditorMode(null);
    setEditingTemplate(null);
  };

  const handleSendTest = async () => {
    if (target === 'user' && userIds.length === 0) {
      alert(t('notificationTemplatesPage.sendTest.enterUserId'));
      return;
    }

    // Check for valid notification content
    const hasTemplate = !!formValue.template;
    const hasDirectContent = formValue.channels.length > 0 && (formValue.content.title || formValue.content.body);

    if (!hasTemplate && !hasDirectContent) {
      alert(t('notificationTemplatesPage.sendTest.selectTemplateOrContent'));
      return;
    }

    try {
      const result = await sendTestMutation.mutateAsync({
        target,
        user_ids: target === 'user' ? userIds : undefined,
        template: formValue.template || undefined,
        context: formValue.template ? formValue.context : undefined,
        channels: formValue.template ? undefined : formValue.channels,
        title: formValue.template ? undefined : formValue.content.title,
        body: formValue.template ? undefined : formValue.content.body,
        icon: formValue.template ? undefined : formValue.content.icon,
        action: formValue.template ? undefined : formValue.content.action,
        action_label: formValue.template ? undefined : formValue.content.action_label,
      });

      setSendResult({
        success: result.success,
        message: result.success
          ? `${t('notificationTemplatesPage.sendTest.sendSuccess')} ${Object.entries(result.channels).map(([k, v]) => `${k}: ${v ? t('notificationTemplatesPage.sendTest.succeeded') : t('notificationTemplatesPage.sendTest.failed')}`).join(', ')}`
          : result.message || t('notificationTemplatesPage.sendTest.sendFailed'),
      });
    } catch (error) {
      setSendResult({
        success: false,
        message: `${t('notificationTemplatesPage.sendTest.sendFailed')}: ${error instanceof Error ? error.message : t('notificationTemplatesPage.sendTest.unknownError')}`,
      });
    }
  };

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="mb-6 max-w-7xl">
        <h1 className="text-2xl font-bold text-gray-900">{t('notificationTemplatesPage.pageTitle')}</h1>
        <p className="text-sm text-gray-500 mt-1">
          {t('notificationTemplatesPage.pageDescription')}
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex">
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-6 py-3 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
              activeTab === 'templates'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <LayoutList className="w-4 h-4" />
            {t('notificationTemplatesPage.tabs.templates')}
          </button>
          <button
            onClick={() => setActiveTab('send-test')}
            className={`px-6 py-3 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
              activeTab === 'send-test'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Send className="w-4 h-4" />
            {t('notificationTemplatesPage.tabs.sendTest')}
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'templates' && (
        <TemplateList
          onCreateClick={handleCreateClick}
          onEditClick={handleEditClick}
        />
      )}

      {activeTab === 'send-test' && (
        <div className="space-y-6">
          {/* Statistics Section */}
          <PushStats />

          {/* Main Content - Two Columns */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Left: Notification Form */}
            <Card>
              <CardHeader
                title={t('notificationTemplatesPage.sendTest.title')}
                description={t('notificationTemplatesPage.sendTest.description')}
              />
              <CardContent className="space-y-6">
                {/* Target Selection */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    {t('notificationTemplatesPage.sendTest.target')}
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setTarget('user')}
                      className={`flex-1 py-2 px-4 rounded-lg border-2 text-sm font-medium transition-all ${
                        target === 'user'
                          ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                          : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {t('notificationTemplatesPage.sendTest.specifyUser')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setTarget('all')}
                      className={`flex-1 py-2 px-4 rounded-lg border-2 text-sm font-medium transition-all ${
                        target === 'all'
                          ? 'bg-amber-50 border-amber-500 text-amber-700'
                          : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {t('notificationTemplatesPage.sendTest.allUsers')}
                    </button>
                  </div>

                  {target === 'user' && (
                    <UserSelector
                      selectedUserIds={userIds}
                      onChange={setUserIds}
                    />
                  )}

                  {target === 'all' && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-700">
                        ⚠️ {t('notificationTemplatesPage.sendTest.allUsersWarning')}
                      </p>
                    </div>
                  )}
                </div>

                {/* Notification Form */}
                <NotificationForm
                  mode="both"
                  value={formValue}
                  onChange={setFormValue}
                />
              </CardContent>

              <CardFooter>
                <div className="flex items-center justify-between w-full">
                  {/* Result message */}
                  {sendResult && (
                    <div className={`flex-1 mr-4 p-3 rounded-lg text-sm ${
                      sendResult.success
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                      {sendResult.message}
                    </div>
                  )}

                  <Button
                    onClick={handleSendTest}
                    loading={sendTestMutation.isPending}
                    disabled={sendTestMutation.isPending || (target === 'user' && userIds.length === 0)}
                  >
                    <Send className="w-4 h-4 mr-1" />
                    {t('notificationTemplatesPage.sendTest.sendButton')}
                  </Button>
                </div>
              </CardFooter>
            </Card>

            {/* Right: Device Tokens & Logs */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Sub-tab Headers */}
              <div className="border-b border-gray-200">
                <nav className="flex">
                  <button
                    onClick={() => setRightPanelTab('tokens')}
                    className={`flex-1 px-4 py-3 border-b-2 font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
                      rightPanelTab === 'tokens'
                        ? 'border-indigo-500 text-indigo-600 bg-indigo-50/50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Smartphone className="w-4 h-4" />
                    {t('notificationTemplatesPage.rightPanel.deviceTokens')}
                  </button>
                  <button
                    onClick={() => setRightPanelTab('logs')}
                    className={`flex-1 px-4 py-3 border-b-2 font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
                      rightPanelTab === 'logs'
                        ? 'border-indigo-500 text-indigo-600 bg-indigo-50/50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <ScrollText className="w-4 h-4" />
                    {t('notificationTemplatesPage.rightPanel.sendLogs')}
                  </button>
                </nav>
              </div>

              {/* Sub-tab Content */}
              <div className="max-h-[600px] overflow-auto">
                {rightPanelTab === 'tokens' && <DeviceTokens />}
                {rightPanelTab === 'logs' && <NotificationLogs />}
              </div>
            </div>
          </div>

          {/* Usage Tips - Collapsed at bottom */}
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-600">
                <span className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
                  {t('notificationTemplatesPage.tips.selectTemplate')}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
                  {t('notificationTemplatesPage.tips.templateVariable')}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
                  {t('notificationTemplatesPage.tips.silentPush')}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Template Editor Modal */}
      <Modal
        isOpen={editorMode !== null}
        onClose={handleEditorClose}
        size="5xl"
      >
        {editorMode && (
          <TemplateEditor
            template={editingTemplate || undefined}
            onClose={handleEditorClose}
            onSuccess={() => {
              // Will auto-close
            }}
          />
        )}
      </Modal>
    </div>
  );
}
